import React, {useEffect} from "react";
import { useDispatch } from "react-redux";
import { getProductData } from "./store/actionCreators/getProductData";
import { sanitiseProductName } from "./helpers/url-helpers";
import ProductTitle from "./components/ProductTitle";
import ProductNav from "./components/ProductNav";
import ProductIntro from "./components/ProductIntro";
import ProductBullets from "./components/ProductBullets";
import ProductFeatures from "./components/Features/ProductFeatures";
import Gallery from "../Gallery/Gallery";
import RelatedProducts from "./components/ProductRelations/RelatedProducts";
import BuyNowLinks from "./components/BuyNow/BuyNowLinks";

import Specification from "./components/Specification/Specification";
import {useTypedSelector} from "./hooks/useTypeSelector";
import Downloads from "./components/Downloads/Downloads";
import {i18n, i18nArray} from "./helpers/localisation-helpers";
import ProductIntroWarranty from "./ProductIntroWarranty";
import ProductCaseStudies from "./components/CaseStudies/ProductCaseStudies";
import ExpertReview from "./components/ExpertReviews/ExpertReview";
import ExpertReviews from "./components/ExpertReviews/ExpertReviews";
import HeroVideo from "./components/HeroVideo/HeroVideo";


interface ProductPageProps {
    apiUrl: string;
    galleryApiUrl: string;
    specificationApiUrl: string;
    productName: string;
    regionCode: string;
    previewToken?: string;
    previewDate?: string;
}

const ProductPage = (props:ProductPageProps) => {
    const dispatch = useDispatch();

    const { error, productData, loaded, pending } = useTypedSelector((state) => state.product);
    
    const [sanitisedProductName, setSanitisedProductName] = React.useState<string>("");
    const [isWarranty, setIsWarranty] = React.useState<boolean>(false);
    const [isEol, setIsEol] = React.useState<boolean>(false);
    
    useEffect(() => {
        dispatch(getProductData(props.productName, props.regionCode, props.apiUrl, props.previewToken, props.previewDate));
    }, [])

    useEffect(() => {
        if(productData != null) {
            setSanitisedProductName(sanitiseProductName(productData.ProductName))
            setIsWarranty(productData.ProductSubType == "Warranty");
            setIsEol(productData.IsEndOfLife);
            document.title = `${productData.ProductNameDisplay} - ${productData.HeroTitle}`;
        }
    }, [productData])

    // React.useEffect(() => {
    //     // declare the data fetching function
    //     const fetchData = async () => {
    //         const result = await axios.post(props.apiUrl, {});
    //         setProduct(result.data);
    //         setSanitisedProductName(sanitiseProductName(result.data.ProductName));
    //         setLoading(false);
    //     }
    //
    //     // call the function
    //     fetchData().catch(console.error);
    // }, []);
    
    if(pending) {
        return (
            <div className="product-page unloaded">
                <ProductTitle
                    starRating={0}
                    productIsDemoable={false}
                    demoUrl={""}
                    energyRating={""}
                    ukEnergyRatingRegUrl={""}
                    euEnergyRatingRegUrl={""}
                    isWarranty={false}
                    extWarrantyId={0}
                    showDealerLocatorUrl={false}
                    dealerLocatorUrl={""}
                />
            </div>
        )
    }

    if(!pending && productData == undefined) {
        return (
            <div>
                Error loading product
            </div>
        )
    }
  
    if(!pending && productData == undefined && error) {
        return (
            <div>
                Error loading product: {error}
            </div>
        )
    }

    return (

        <div className="product-page">

            {productData.IsEndOfLife &&
                <div className="end-of-life">
                                <p>{i18nArray("Resource.ModelDiscontinuedMaybeUnavailable", 
                                    productData.PageLocalisations, productData.ProductNameDisplay)}</p>
                </div>
            }

            <ProductTitle
                hasBuyNowLinks={productData.BuyNowLinks && productData.BuyNowLinks.length > 0}
                starRating={3}
                productIsDemoable={productData.ProductIsDemoable}
                demoUrl={productData.DemoUrl}
                energyRating={productData.EnergyRating}
                ukEnergyRatingRegUrl={productData.UKRegEnergyRatingCertWebUrl}
                euEnergyRatingRegUrl={productData.EURegEnergyRatingCertWebUrl}
                isWarranty={isWarranty}
                extWarrantyId={productData.ExtWarrantyId}
                showDealerLocatorUrl={productData.ShowDealerLocatorUrl}
                dealerLocatorUrl={productData.DealerLocatorUrl}
            />

            {!isWarranty && !isEol && <ProductNav sanitisedProductName={sanitisedProductName} />}

            {!isEol && <HeroVideo siteOrigin={productData.SiteDomain} />}
            
            {!isWarranty && !isEol && <ProductIntro 
                heroTitle={productData.HeroTitle}
                description={productData.Description}
                largeThumbFilename={productData.LargeThumbUrl} 
                terms={productData.Terms}
            />}

            {isWarranty && !isEol && <ProductIntroWarranty 
                heroTitle={productData.HeroTitle}
                description={productData.Description} />}

            {!isWarranty && !isEol && <ProductBullets />}

            {!isWarranty && !isEol && <ProductFeatures 
                features={productData.DynoFeatures}
                localisedProductName={productData.ProductName} />}

            {!isEol &&
                <React.Fragment>
                    
                    <a id="accessories"></a>
                    <RelatedProducts
                        products={productData.RecommendedProducts} 
                        sectionTitle={i18n("Resource.Accessories", productData.PageLocalisations)}
                    />
        
                    <a id="related-products"></a>
                    <RelatedProducts
                        products={productData.AccessoryToProducts}
                        sectionTitle={i18n("Resource.compatmodels", productData.PageLocalisations)}
                    />
                </React.Fragment>
            }

            {!isWarranty && !isEol && <BuyNowLinks buyNowLinks={productData.BuyNowLinks} />}

            {!isWarranty && !isEol && productData.ExpertReviews != undefined && productData.ExpertReviews != null && productData.ExpertReviews.length > 0 && <ExpertReviews />}

            {!isWarranty && !isEol && productData.CaseStudies != undefined && productData.CaseStudies != null && productData.CaseStudies.length > 0 && <ProductCaseStudies />}
     
            {!isWarranty && !isEol && <a id="gallery"></a>}
            {!isWarranty && !isEol && <Gallery
                apiUrl={props.galleryApiUrl} 
                sectionTitle={i18n("Resource.Gallery", productData.PageLocalisations)}
                productName={props.productName} 
                regionCode={props.regionCode}/>}


            {!isWarranty && !isEol && <Specification 
                productName={productData.ProductName}
                regionCode={productData.SiteRegion}
                specApiUrl={props.specificationApiUrl} />}

            {!isWarranty && <Downloads />}
    
        </div>
    )
}

export default ProductPage;